import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BoardPreviewMap, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import setField from 'webapp/helpers/set-field';
import ClipboardCopyComponent from 'webapp/components/clipboard-copy';
import type { Model } from 'webapp/components/common/share/link/index';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormTextareaComponent from 'webapp/components/form/textarea';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Report from 'webapp/models-custom/report';

const enum SizeOption {
	RESPONSIVE = 1,
	FIXED = 2,
}

interface Args {
	model: Model; // Board | BoardLoop | Report
	isContentShown: boolean;
	openLinkTab: () => void;
	onClose: () => void;
	previewHash?: BoardPreviewMap;
}

export default class CommonShareEmbedComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked sizeOption = SizeOption.RESPONSIVE;

	@tracked width = 640;
	@tracked height = 405;

	sizeOptions = [
		{ value: SizeOption.RESPONSIVE, label: 'Responsive' },
		{ value: SizeOption.FIXED, label: 'Fixed' },
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.isReport) {
			this.sizeOption = SizeOption.FIXED;
		}
	}

	get isReport() {
		return this.args.model instanceof Report;
	}

	get isResponsive() {
		return this.sizeOption === SizeOption.RESPONSIVE;
	}

	get isTabDisabled() {
		return !this.args.model.isPublicEnabled;
	}

	get datawallUrl() {
		return this.args.model.getDatawallUrlWithPreviewHash(this.args.previewHash, true);
	}

	get code() {
		let iframeStyle;
		if (this.isResponsive) {
			iframeStyle = 'style="position:absolute; top:0; left:0; width:100%; height:100%;"';
		} else {
			iframeStyle = `width="${this.width}" height="${this.height}"`;
		}

		const ratio = 63; //56.25, // in %
		let code = `<iframe src="${this.datawallUrl}" ${iframeStyle} frameborder="0" allowfullscreen></iframe>`;

		if (this.isResponsive) {
			code = `<div style="padding:${ratio}% 0 0 0; position:relative;">${code}</div>`;
		}

		return code;
	}

	get getMessage(): string {
		return this.isReport
			? 'Copy this HTML code to embed a live report:'
			: 'Copy this HTML code to embed a live dashboard:';
	}

	@action
	changeSizeOption(sizeOption: AnySimpleValue) {
		this.sizeOption = sizeOption as SizeOption;
	}

	@action
	copyToClipboard() {
		this.infolayerService.ok('Embed code copied to clipboard!');
		this.analyticsService.event(TrackingEvent.SHARE_PUBLIC_URL_BY_EMBED);
	}
	<template>
		<div
			class="content2
				{{if @isContentShown 'content2--shown'}}
				embed
				{{if this.isTabDisabled 'disabled'}}"
		>

			{{#if this.isTabDisabled}}
				<p class="disabled-warning">
					<span>
						To embed this dashboard, turn on the
						<strong>public link</strong>
						on the ‘Shareable link’ tab.
					</span>
					<a class="button size-tiny" {{on "click" @openLinkTab}}>Open shareable link</a>
				</p>
			{{/if}}

			<p>{{this.getMessage}}</p>

			<div class="row code">
				<FormTextareaComponent
					name="code"
					@value={{this.code}}
					readonly="readonly"
					@selectAllOnClick={{true}}
					@autofocus={{true}}
				/>
			</div>

			<div class="row radiobuttons">
				{{#unless this.isReport}}
					<label>Size</label>
					<div class="right">
						<FormRadiobuttonGroupComponent
							class="radiobutton-default"
							@name="sizeOption"
							@selected={{this.sizeOption}}
							@options={{this.sizeOptions}}
							@onChange={{this.changeSizeOption}}
						/>
					</div>
				{{/unless}}

				<div class="size {{if this.isResponsive 'disabled'}}">
					{{#if this.isReport}}
						<span class="width">Frame width</span>
					{{/if}}
					<FormInputComponent
						@type="number"
						name="width"
						@value={{this.width}}
						@onEmpty={{setField target=this property="width"}}
						disabled={{this.isResponsive}}
					/>
					{{#if this.isReport}}
						<span class="height">Frame height</span>
					{{else}}
						<span class="x">x</span>
					{{/if}}
					<FormInputComponent
						@type="number"
						name="height"
						@value={{this.height}}
						@onEmpty={{setField target=this property="height"}}
						disabled={{this.isResponsive}}
					/>
					{{#unless this.isReport}}
						<span class="px">pixels</span>
					{{/unless}}
				</div>
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{@onClose}}
					/>
					<a role="button" class="button">
						Copy code
						<ClipboardCopyComponent
							@text={{this.code}}
							@onAfterCopy={{this.copyToClipboard}}
						/>
					</a>
				</div>
			</div>
		</div>
	</template>
}
